// All tally-based survey data: questions, options, result types
// Sources cited per survey. All text in English (displayed in English on the platform).

import { SurveyTypes } from '../types/Survey.ts'
import type { TallySurveyDefinition } from '../types/surveys/TallySurvey.ts'

// ---------------------------------------------------------------------------
// SURVEY 1 – Goleman's 6 Leadership Styles
// Source: Daniel Goleman, Primal Leadership (2002)
// ---------------------------------------------------------------------------

export const LEADERSHIP_STYLES_DATA: TallySurveyDefinition = {
    surveyType: SurveyTypes.LEADERSHIP_STYLES,
    title: 'Štýly vedenia',
    subtitle: 'Goleman',
    slug: 'styly-vedenia',
    icon: '👔',
    duration: '10',
    description:
        'Zisti, aký je tvoj dominantný štýl vedenia podľa Daniela Golemana. Každý štýl má svoje silné stránky a riziká – najúčinnejší lídri flexibilne kombinujú viaceré.',
    types: {
        visionary: {
            key: 'visionary',
            label: 'Vizionársky',
            emoji: '🔭',
            color: 'text-purple-600',
            bgColor: 'bg-purple-500',
            barColor: 'bg-purple-500',
            description:
                'Inšpiruješ presvedčivou víziou budúcnosti a dávaš ľuďom slobodu nájsť vlastnú cestu. Motto: „Poďte so mnou." Najúčinnejší pri zmene alebo keď je potrebný nový smer. Formuluješ, kam skupina smeruje, ale nie ako – oslobodzuješ ľudí k inováciám a kalkulovanému riziku.',
            characteristics: [
                'Formuluješ jasný a inšpirujúci smer',
                'Dávaš tímu autonómiu v spôsobe práce',
                'Prepájaš každodenné úlohy s vyšším zmyslom',
                'Prirodzene budíš nadšenie a oddanosť',
                'Najefektívnejší pri organizačných zmenách'
            ],
            tips: [
                'Dopĺňaj víziu konkrétnymi mílnikmi, aby ľudia videli pokrok',
                'Aktívne počúvaj spätnú väzbu – vízia sa musí vyvíjať',
                'Kombinuj s koučovacím štýlom pre individuálny rozvoj',
                'Nezabudni na „ako" – niektorí ľudia potrebujú viac štruktúry'
            ]
        },
        coaching: {
            key: 'coaching',
            label: 'Koučovací',
            emoji: '🌱',
            color: 'text-green-600',
            bgColor: 'bg-green-500',
            barColor: 'bg-green-500',
            description:
                'Investuješ do dlhodobého rastu ľudí a prepájaš každodennú prácu s osobnými rozvojovými cieľmi. Motto: „Skús toto." Najúčinnejší s motivovanými ľuďmi, ktorí chcú rásť. Riziko: je to časovo náročné a zlyhá, keď človek nie je ochotný sa meniť.',
            characteristics: [
                'Kladieš silné otázky namiesto dávania odpovedí',
                'Vidíš potenciál v každom člene tímu',
                'Prepájaš denné úlohy s kariérnymi ambíciami',
                'Trpezlivo sprevádzaš ľudí cez výzvy',
                'Buduješ atmosféru psychologickej bezpečnosti'
            ],
            tips: [
                'Rozpoznaj, kedy koučovanie nie je vhodné (kríza, neochota)',
                'Stanovuj jasné očakávania – koučovanie nie je absencia štandardov',
                'Kombinuj so štýlom udávajúcim tempo, keď tím potrebuje tlak na výkon',
                'Uchovávaj si zoznam rozvojových cieľov pre každého člena tímu'
            ]
        },
        affiliative: {
            key: 'affiliative',
            label: 'Afiliačný',
            emoji: '🤝',
            color: 'text-pink-600',
            bgColor: 'bg-pink-500',
            barColor: 'bg-pink-500',
            description:
                'Buduješ harmóniu a emocionálne väzby. Ľudia na prvom mieste – vždy. Motto: „Ľudia sú najdôležitejší." Najúčinnejší počas stresu, konfliktu alebo pri obnove dôvery. Riziko: vyhýbanie sa ťažkej spätnej väzbe, pokles výkonu.',
            characteristics: [
                'Vytváraš bezpečné a dôveryhodné prostredie',
                'Riešiš konflikty diplomaticky a s empatiou',
                'Ľudia sa okolo teba cítia vypočutí a cenení',
                'Prioritizuješ tímovú kohéziu nad individuálne výsledky',
                'Dokážeš uzdraviť rozbitý tím'
            ],
            tips: [
                'Precvičuj dávanie konštruktívnej spätnej väzby – vyhýbanie sa jej škodí',
                'Kombinuj s vizionárskym štýlom alebo štýlom udávajúcim tempo pre rovnováhu',
                'Nastav jasné výkonnostné štandardy aj v priateľskom prostredí',
                'Sleduj, či harmónia nezakrýva neriešené problémy'
            ]
        },
        democratic: {
            key: 'democratic',
            label: 'Demokratický',
            emoji: '🗳️',
            color: 'text-blue-600',
            bgColor: 'bg-blue-500',
            barColor: 'bg-blue-500',
            description:
                'Buduješ konsenzus cez inklúziu. Úprimne sa pýtaš: Čo si myslíš? Najúčinnejší pri budovaní podpory alebo zbieraní rôznych perspektív. Riziko: pomalé rozhodovanie v urgentných situáciách.',
            characteristics: [
                'Aktívne zapájaš všetkých do rozhodovania',
                'Ceníš si rôznorodosť názorov',
                'Buduješ silný pocit vlastníctva rozhodnutí',
                'Využívaš kolektívnu inteligenciu tímu',
                'Demokratické rozhodnutia majú silnú podporu pri implementácii'
            ],
            tips: [
                'V krízových situáciách buď pripravený rozhodnúť sám',
                'Stanovuj jasné deadliny na rozhodnutia – konsenzus nemôže trvať večne',
                'Rozlišuj medzi rozhodnutiami vyžadujúcimi konsenzus a tými, kde stačí input',
                'Komunikuj transparentne, keď musíš rozhodnúť proti väčšine'
            ]
        },
        pacesetting: {
            key: 'pacesetting',
            label: 'Udávajúci tempo',
            emoji: '🏃',
            color: 'text-orange-600',
            bgColor: 'bg-orange-500',
            barColor: 'bg-orange-500',
            description:
                'Nastavuješ vysokú latku a vedieš príkladom. Motto: „Rob to ako ja, teraz." Najúčinnejší s vysoko kvalifikovanými, samo-motivovanými tímami. Používaj striedmo – Goleman zistil, že tento štýl najčastejšie „otrávi klímu" vyhorením a nedostatkom vedenia.',
            characteristics: [
                'Nastavuješ extrémne vysoké štandardy – pre seba aj tím',
                'Vedieš osobným príkladom a nasadením',
                'Rýchlo identifikuješ slabý výkon a vyžaduješ nápravu',
                'Očakávaš autonómiu a iniciatívu od tímu',
                'Dosahuješ vynikajúce krátkodobé výsledky'
            ],
            tips: [
                'Sleduj varovné signály vyhorenia v tíme',
                'Kombinuj s koučovacím štýlom – nie každý sa učí pozorovaním',
                'Verbalizuj svoje štandardy a očakávania – ľudia nie sú telepati',
                'Niekedy je „dosť dobré" skutočne dosť dobré'
            ]
        },
        commanding: {
            key: 'commanding',
            label: 'Direktívny',
            emoji: '⚡',
            color: 'text-red-600',
            bgColor: 'bg-red-500',
            barColor: 'bg-red-500',
            description:
                'Vydávaš jasné smernice a vyžaduješ zosúladenie. Motto: „Rob, čo ti hovorím." Najúčinnejší v skutočných krízach alebo pri ozdravovaní. Nadmerné používanie potláča inováciu, znižuje morálku a vyvoláva odpor.',
            characteristics: [
                'Rozhoduješ rýchlo a s istotou',
                'Poskytuješ jasnú štruktúru v chaose',
                'Dokážeš stabilizovať situáciu v kríze',
                'Vyžaduješ disciplínu a dodržiavanie pravidiel',
                'Efektívny pri urgentných a život-ohrozujúcich situáciách'
            ],
            tips: [
                'Tento štýl by mal byť výnimkou, nie normou',
                'Po kríze prejdi na iný štýl – dlhodobý commanding ničí tím',
                'Aj v kríze vysvetľuj „prečo", nielen „čo"',
                'Reflektuj, či tvoja direktívnosť nie je maska pre strach z neistoty'
            ]
        }
    },
    questions: [
        // 6 provided questions
        {
            title: 'Keď je potrebná veľká zmena, najprirozenejšie...',
            options: [
                { text: 'Namaľujem živý obraz toho, kam smerujeme a prečo to dáva zmysel', typeKey: 'visionary' },
                { text: 'Pomôžem každému prepojiť zmenu s jeho vlastným rastom', typeKey: 'coaching' },
                {
                    text: 'Sústredím sa na morálku tímu a uistím sa, že sa nikto necíti stratený',
                    typeKey: 'affiliative'
                },
                { text: 'Zapojím všetkých do tvorby plánu zmeny spoločne', typeKey: 'democratic' },
                { text: 'Stanovím vysoké štandardy a povediem zmenu príkladom', typeKey: 'pacesetting' },
                { text: 'Dám jasné inštrukcie a očakávam, že sa budú dodržiavať', typeKey: 'commanding' }
            ]
        },
        {
            title: 'V rozhovore o výkone je môj prvý inštinkt...',
            options: [
                { text: 'Prepojiť ich rolu s väčšou misiou, ktorej sú súčasťou', typeKey: 'visionary' },
                { text: 'Klásť otázky na odhalenie ich ambícií a oblastí rastu', typeKey: 'coaching' },
                {
                    text: 'Najprv ich uistiť, že sú v bezpečí a cenení, pred diskusiou o medzerách',
                    typeKey: 'affiliative'
                },
                { text: 'Požiadať o ich sebahodnotenie predtým, než zdieľam svoje', typeKey: 'democratic' },
                { text: 'Byť priamy ohľadom rozdielu medzi aktuálnym a očakávaným výkonom', typeKey: 'pacesetting' },
                { text: 'Jasne povedať, čo sa musí zmeniť a do kedy', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Môj tím zvyčajne opisuje moje rozhodovanie ako...',
            options: [
                { text: 'Orientované na budúcnosť – vždy ukotvené v dlhodobej vízii', typeKey: 'visionary' },
                { text: 'Rozvojové – vždy sa pýta, čo pomôže ľuďom rásť', typeKey: 'coaching' },
                { text: 'Vzťahové – záleží mu na tom, ako sa tím cíti', typeKey: 'affiliative' },
                { text: 'Inkluzívne – hľadá input pred rozhodnutím', typeKey: 'democratic' },
                { text: 'Orientované na výsledky – ambiciózne ciele a meranie všetkého', typeKey: 'pacesetting' },
                { text: 'Rozhodné – jasné, rýchle, očakáva zosúladenie', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Keď člen tímu má problémy, moja štandardná reakcia je...',
            options: [
                { text: 'Pripomenúť im, prečo je ich práca dôležitá v kontexte celku', typeKey: 'visionary' },
                { text: 'Preskúmať príčiny a spoločne navrhnúť plán rastu', typeKey: 'coaching' },
                { text: 'Najprv sa opýtať na ich emócie a ponúknuť podporu', typeKey: 'affiliative' },
                { text: 'Zozbierať perspektívu tímu, ako pomôcť', typeKey: 'democratic' },
                { text: 'Ukázať im, ako by som to riešil ja, a zvýšiť očakávania', typeKey: 'pacesetting' },
                { text: 'Dať priamu spätnú väzbu na to, čo sa musí zmeniť', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Ako líder sa cítim najviac nabudený, keď...',
            options: [
                { text: 'Komunikujem víziu a inšpirujem ľudí k niečomu zmysluplnému', typeKey: 'visionary' },
                { text: 'Sledujem, ako niekto rastie vďaka rozhovoru, ktorý sme mali', typeKey: 'coaching' },
                { text: 'Vytváram teplú, dôveryhodnú a spolupracujúcu atmosféru', typeKey: 'affiliative' },
                { text: 'Facilitujem skvelú diskusiu, ktorá vedie k spoločnému rozhodnutiu', typeKey: 'democratic' },
                { text: 'Tlačím tím k prekonaniu vlastných rekordov', typeKey: 'pacesetting' },
                { text: 'Prerezávam sa šumom a dostávam ľudí do pohybu rýchlo', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Čo najlepšie vystihuje tvoj pohľad na autoritu?',
            options: [
                { text: 'Autorita pochádza z presvedčivej vízie, ktorú ľudia chcú nasledovať', typeKey: 'visionary' },
                { text: 'Autorita sa buduje skutočným investovaním do rastu iných', typeKey: 'coaching' },
                { text: 'Autorita stojí na dôvere, starostlivosti a emocionálnom spojení', typeKey: 'affiliative' },
                { text: 'Autorita je legitímna len vtedy, keď tí, ktorých sa týka, majú hlas', typeKey: 'democratic' },
                {
                    text: 'Autorita pochádza z preukázanej excelencie, ktorú chcú ostatní napodobniť',
                    typeKey: 'pacesetting'
                },
                { text: 'Autorita je zodpovednosťou lídra, ktorú treba rozhodne vykonávať', typeKey: 'commanding' }
            ]
        },
        // 6 extended questions for deeper assessment
        {
            title: 'Keď nastavuješ ciele pre tím, zvyčajne...',
            options: [
                { text: 'Formulujem inšpiratívne „moonshot" ciele prepojené s naším poslaním', typeKey: 'visionary' },
                { text: 'Nastavujem individuálne rozvojové ciele šité na mieru každého člena', typeKey: 'coaching' },
                { text: 'Uistím sa, že ciele nespôsobia stres a tím sa cíti komfortne', typeKey: 'affiliative' },
                { text: 'Spoločne definujeme ciele tak, aby každý cítil vlastníctvo', typeKey: 'democratic' },
                { text: 'Stanovím ambiciózne metriky a osobne idem príkladom', typeKey: 'pacesetting' },
                { text: 'Jasne priradím ciele, zodpovednosti a deadliny', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Uprostred krízy (napr. odchod kľúčového klienta) tvoj prvý krok je...',
            options: [
                { text: 'Reframovať krízu ako príležitosť a ukázať novú víziu', typeKey: 'visionary' },
                { text: 'Využiť krízu ako učebnú skúsenosť pre rast tímu', typeKey: 'coaching' },
                { text: 'Zhromaždiť tím, uistiť ich a posilniť vzťahy', typeKey: 'affiliative' },
                { text: 'Zvolať poradu a spoločne hľadať riešenie', typeKey: 'democratic' },
                {
                    text: 'Okamžite pracovať na riešení a očakávať rovnaké nasadenie od všetkých',
                    typeKey: 'pacesetting'
                },
                { text: 'Prevziať kontrolu, rozdeliť úlohy a riadiť situáciu', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Pri onboardingu nového člena tímu kladieš dôraz na...',
            options: [
                { text: 'Vysvetlenie, kam smerujeme a prečo ich rola je dôležitá pre celok', typeKey: 'visionary' },
                { text: 'Zmapovanie ich silných stránok a vytvorenie osobného rozvojového plánu', typeKey: 'coaching' },
                { text: 'Predstavenie tímu, budovanie vzťahov a vytvorenie pocitu prijatia', typeKey: 'affiliative' },
                { text: 'Zapojenie tímu do onboardingu a počúvanie nových perspektív', typeKey: 'democratic' },
                { text: 'Rýchle zaučenie a očakávanie, že čoskoro budú na úrovni ostatných', typeKey: 'pacesetting' },
                { text: 'Jasné vysvetlenie pravidiel, procesov a očakávaní', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Spôsob, akým zvyčajne dávaš uznanie, je...',
            options: [
                { text: 'Verejne spojím ich prínos s napĺňaním našej vízie', typeKey: 'visionary' },
                { text: 'Osobný rozhovor o tom, ako sa posunuli a ako ďalej rásť', typeKey: 'coaching' },
                { text: 'Oslava v tíme – spoločné uznanie a emocionálna podpora', typeKey: 'affiliative' },
                { text: 'Nechám tím navzájom oceňovať a zdieľať úspechy', typeKey: 'democratic' },
                { text: 'Uznám výnimočný výkon a postavím ho ako vzor pre ostatných', typeKey: 'pacesetting' },
                { text: 'Stručné „dobrá práca" a pokračujem k ďalšej úlohe', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Keď delegujem úlohu, typicky...',
            options: [
                { text: 'Vysvetlím „prečo" a nechám ich nájsť „ako"', typeKey: 'visionary' },
                { text: 'Vyberiem úlohu, ktorá ich posunie v rozvoji, a ponúknem mentoring', typeKey: 'coaching' },
                { text: 'Vyberiem niekoho, kto to zvládne bez stresu, a ponúknem pomoc', typeKey: 'affiliative' },
                { text: 'Otvorene sa opýtam, kto sa chce ujať úlohy', typeKey: 'democratic' },
                { text: 'Delegujem len to, čo viem, že urobia na mojej úrovni kvality', typeKey: 'pacesetting' },
                { text: 'Jasne priradím zodpovednosť, termín a kontrolné body', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Pri komunikácii stratégie tímu sa sústredíš na...',
            options: [
                { text: 'Príbeh o tom, kam ideme a prečo je to dôležité pre svet', typeKey: 'visionary' },
                { text: 'Ako stratégia vytvorí príležitosti na osobný a profesijný rast', typeKey: 'coaching' },
                { text: 'Ako stratégia ovplyvní ľudí a čo urobíme, aby sa cítili dobre', typeKey: 'affiliative' },
                { text: 'Otvorený dialóg o stratégii s možnosťou ju spoločne doladiť', typeKey: 'democratic' },
                { text: 'KPI, míľniky a čo presne musíme dosiahnuť', typeKey: 'pacesetting' },
                { text: 'Jasný plán s priradeniami a termínmi – bez zbytočných diskusií', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Keď tím stráca energiu počas dlhého projektu, zvyčajne...',
            options: [
                { text: 'Pripomeniem, kam celý projekt smeruje a prečo na ňom záleží', typeKey: 'visionary' },
                {
                    text: 'Hľadám, čo sa každý potrebuje naučiť alebo posilniť, aby sa pohol ďalej',
                    typeKey: 'coaching'
                },
                { text: 'Zameriam sa na náladu v tíme a obnovu vzájomnej podpory', typeKey: 'affiliative' },
                { text: 'Otvorím diskusiu o tom, čo tím potrebuje zmeniť v spôsobe práce', typeKey: 'democratic' },
                { text: 'Ukážem vlastným tempom, že sa dá znova zabrať a posunúť výkon', typeKey: 'pacesetting' },
                {
                    text: 'Určím priority, rozdelím ďalšie kroky a nastavím jasný rytmus kontroly',
                    typeKey: 'commanding'
                }
            ]
        },
        {
            title: 'Keď sa objaví nový nápad s neistým výsledkom, moja reakcia je...',
            options: [
                { text: 'Skúmam, či zapadá do väčšieho smerovania, ktoré chceme budovať', typeKey: 'visionary' },
                { text: 'Premýšľam, komu by dal príležitosť rozvinúť nové schopnosti', typeKey: 'coaching' },
                { text: 'Všímam si, ako by ovplyvnil dôveru, vzťahy a stabilitu tímu', typeKey: 'affiliative' },
                { text: 'Pozvem tím, aby zhodnotil prínosy, riziká a možné úpravy', typeKey: 'democratic' },
                { text: 'Rýchlo testujem, či vie priniesť merateľne lepší výkon', typeKey: 'pacesetting' },
                { text: 'Rozhodnem, či má dostať priestor, a určím hranice experimentu', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Keď tím potrebuje spätnú väzbu po neúspechu, najskôr...',
            options: [
                { text: 'Prepojím poučenie s tým, akým tímom sa chceme stať', typeKey: 'visionary' },
                { text: 'Pomôžem ľuďom pomenovať, čo sa naučili a čo si chcú trénovať', typeKey: 'coaching' },
                { text: 'Vytvorím priestor, aby sa ľudia cítili vypočutí a neostali v napätí', typeKey: 'affiliative' },
                { text: 'Facilitujem spoločnú reflexiu, kde každý prispeje svojím pohľadom', typeKey: 'democratic' },
                { text: 'Zameriam sa na konkrétne štandardy, ktoré nabudúce musíme splniť', typeKey: 'pacesetting' },
                { text: 'Pomenujem, čo sa stalo, čo sa mení a kto za čo preberá zodpovednosť', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Keď máš v tíme silného experta, ktorý chce veľa autonómie...',
            options: [
                { text: 'Dám mu smer a nechám ho nájsť najlepší spôsob, ako k nemu dôjsť', typeKey: 'visionary' },
                { text: 'Použijem jeho ambíciu ako základ pre dlhodobejší rozvoj', typeKey: 'coaching' },
                { text: 'Dbám, aby jeho samostatnosť nenarušila vzťahy a spoluprácu v tíme', typeKey: 'affiliative' },
                { text: 'Zapojím tím do dohody o tom, kde autonómia začína a končí', typeKey: 'democratic' },
                { text: 'Nastavím vysokú latku a očakávam, že bude ťahať výkon dopredu', typeKey: 'pacesetting' },
                { text: 'Jasne určím rozhodovacie právomoci, výstupy a termíny', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Keď sa tím nevie dohodnúť na prioritách, typicky...',
            options: [
                { text: 'Vrátim diskusiu k tomu, ktorý smer najviac podporuje našu víziu', typeKey: 'visionary' },
                { text: 'Pýtam sa, čo jednotlivé priority znamenajú pre rast ľudí a tímu', typeKey: 'coaching' },
                { text: 'Snažím sa najprv znížiť napätie a obnoviť ochotu spolupracovať', typeKey: 'affiliative' },
                { text: 'Pomôžem tímu spoločne vybrať kritériá a podľa nich sa rozhodnúť', typeKey: 'democratic' },
                { text: 'Vyberiem priority, ktoré najviac posunú výsledky a výkon', typeKey: 'pacesetting' },
                { text: 'Rozhodnem poradie priorít a jasne vysvetlím, čo ide bokom', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Pri plánovaní rozvoja tímu najviac sledujem...',
            options: [
                { text: 'Či nové schopnosti podporia smer, ktorým sa tím potrebuje vydať', typeKey: 'visionary' },
                { text: 'Individuálne ambície, silné stránky a ďalší rast každého človeka', typeKey: 'coaching' },
                { text: 'Dôveru, súdržnosť a schopnosť ľudí fungovať spolu v záťaži', typeKey: 'affiliative' },
                { text: 'To, aby tím spoluvytváral, čo sa potrebuje naučiť a prečo', typeKey: 'democratic' },
                { text: 'Schopnosti, ktoré najrýchlejšie zvýšia kvalitu a tempo výsledkov', typeKey: 'pacesetting' },
                { text: 'Kritické medzery, ktoré treba rýchlo uzavrieť jasným tréningom', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Keď musíš komunikovať nepopulárne rozhodnutie...',
            options: [
                { text: 'Ukážem, ako rozhodnutie súvisí s dlhodobým smerom a zmyslom práce', typeKey: 'visionary' },
                { text: 'Vysvetlím, čo sa z neho môžeme naučiť a ako v ňom ľudia môžu rásť', typeKey: 'coaching' },
                { text: 'Uznám emócie tímu a dám priestor spracovať dopad rozhodnutia', typeKey: 'affiliative' },
                { text: 'Vysvetlím proces, vypočujem reakcie a hľadám, čo ešte vieme upraviť', typeKey: 'democratic' },
                { text: 'Zdôrazním výkonové dôvody a čo musíme doručiť napriek nepohodliu', typeKey: 'pacesetting' },
                { text: 'Poviem rozhodnutie priamo, určím ďalšie kroky a očakávania', typeKey: 'commanding' }
            ]
        },
        {
            title: 'Keď nový projekt nemá jasného vlastníka, najskôr...',
            options: [
                {
                    text: 'Pomenujem, prečo projekt stojí za pozornosť a aký výsledný obraz sledujeme',
                    typeKey: 'visionary'
                },
                { text: 'Hľadám človeka, pre ktorého je projekt vhodnou rozvojovou výzvou', typeKey: 'coaching' },
                { text: 'Zisťujem, kto má kapacitu bez toho, aby sa narušila pohoda tímu', typeKey: 'affiliative' },
                { text: 'Otvorím tímovú dohodu o tom, kto sa vlastníctva ujme', typeKey: 'democratic' },
                {
                    text: 'Vyberiem najvýkonnejšieho človeka, ktorý vie projekt rýchlo rozbehnúť',
                    typeKey: 'pacesetting'
                },
                { text: 'Pridelím vlastníka, rozsah zodpovednosti a prvý kontrolný bod', typeKey: 'commanding' }
            ]
        }
    ]
}

// ---------------------------------------------------------------------------
// SURVEY 2 – Laloux's 5 Organizational Paradigms
// Source: Frédéric Laloux, Reinventing Organizations (2014)
// ---------------------------------------------------------------------------

export const LALOUX_PARADIGMS_DATA: TallySurveyDefinition = {
    surveyType: SurveyTypes.LALOUX_PARADIGMS,
    title: 'Organizačná kultúra',
    subtitle: 'Laloux',
    slug: 'organizacne-paradigmy',
    icon: '🏢',
    duration: '8',
    description:
        'Zisti, v akom type organizačnej kultúry sa nachádzaš podľa Frédérica Laloux. Každá farba reprezentuje inú úroveň organizačného vedomia – od moci cez pravidlá po živý organizmus.',
    types: {
        red: {
            key: 'red',
            label: '🔴 Červená',
            emoji: '🔴',
            color: 'text-red-600',
            bgColor: 'bg-red-500',
            barColor: 'bg-red-500',
            description:
                'Založená na moci. Autorita vynucovaná strachom a ohromujúcou osobnou silou. Krátkodobá, reaktívna, predátorská dynamika. Šéf musí neustále vykonávať moc, inak ju stratí. Príklady: pouličné gangy, mafie, niektoré rané rodinné firmy.',
            characteristics: [
                'Rozhodnutia robí najsilnejší v miestnosti',
                'Lojalita sa vynucuje strachom alebo odmenou',
                'Žiadne formálne procesy – vládne osobná sila',
                'Reaktívne riešenie problémov, krátkodobé myslenie',
                'Vysoká centralizácia moci u jednej osoby'
            ],
            tips: [
                'Uvedom si, že strach ako nástroj motivácie má krátkodobú účinnosť',
                'Zavádzaj aspoň základné procesy a transparentnosť',
                'Pracuj na budovaní dôvery namiesto vynucovania kontroly',
                'Hľadaj mentora z organizácie na vyššej úrovni'
            ]
        },
        amber: {
            key: 'amber',
            label: '🟤 Jantárová',
            emoji: '🟤',
            color: 'text-amber-600',
            bgColor: 'bg-amber-500',
            barColor: 'bg-amber-500',
            description:
                'Stabilita cez procesy, pravidlá a rigidnú hierarchiu. Formálne roly v prísnom reťazci velenia. Dlhodobé plánovanie je možné, ale konformita je cenená viac ako inovácia. Budúcnosť je opakovaním minulosti. Príklady: armáda, cirkvi, štátne úrady, tradičné školy.',
            characteristics: [
                'Jasná hierarchia a reťazec velenia',
                'Formálne procesy a schvaľovacie procedúry',
                'Stabilita a predvídateľnosť sú prioritou',
                'Identita je viazaná na rolu v systéme',
                'Zmena je pomalá a riešená cez správne kanály'
            ],
            tips: [
                'Oceň stabilitu, ktorú tento systém prináša – nie je bezcenná',
                'Hľadaj priestor pre inováciu v rámci existujúcich pravidiel',
                'Experimentuj s delegovaním rozhodnutí na nižšie úrovne',
                'Skús zaviesť retrospektívy alebo slučky spätnej väzby'
            ]
        },
        orange: {
            key: 'orange',
            label: '🟠 Oranžová',
            emoji: '🟠',
            color: 'text-orange-600',
            bgColor: 'bg-orange-500',
            barColor: 'bg-orange-500',
            description:
                'Výkon-orientovaná meritokracia. Organizácia je stroj na optimalizáciu. Inovácia a súťaž sú primárnymi motormi. Riadenie cez ciele, KPI, bonusy. Príklady: väčšina nadnárodných korporácií, investičné banky, konzultačné firmy.',
            characteristics: [
                'Rozhodnutia robí ten, kto je zodpovedný za výsledky',
                'Bonusy, povýšenia a uznanie za výkon',
                'Inovácia a konkurenčná výhoda sú prioritou',
                'KPI, OKR a dátami riadené rozhodovanie',
                'Pragmatický prístup k riešeniu problémov'
            ],
            tips: [
                'Sleduj, či súťaž neničí spoluprácu a tímového ducha',
                'Nezabudni na pohodu ľudí – nie sú len „ľudské zdroje"',
                'Skús merať aj kultúru, angažovanosť a zmysluplnosť, nie len výkon',
                'Experimentuj s princípmi služobného vedenia'
            ]
        },
        green: {
            key: 'green',
            label: '🟢 Zelená',
            emoji: '🟢',
            color: 'text-green-600',
            bgColor: 'bg-green-500',
            barColor: 'bg-green-500',
            description:
                "Ľudia na prvom mieste, hodnotovo orientovaná. Zameranie na zainteresované strany, služobné vedenie, posilňovanie ľudí a kultúra sú rovnako dôležité ako výsledky. Rozhodovanie konsenzom. Príklady: Patagonia, Southwest Airlines, Ben & Jerry's.",
            characteristics: [
                'Hodnoty a kultúra sú rovnako dôležité ako zisky',
                'Služobné vedenie – líder slúži tímu',
                'Posilňovanie a zapojenie zamestnancov',
                'Rovnováha medzi zainteresovanými stranami, nie len akcionármi',
                'Dôraz na diverzitu, inklúziu a sociálnu zodpovednosť'
            ],
            tips: [
                'Dávaj pozor, aby konsenzus nespomalil rozhodovanie príliš',
                'Nastavuj jasné zodpovednosti – posilňovanie ľudí nie je absencia štruktúry',
                'Buď otvorený aj voči tým, ktorí preferujú jasné smerovanie',
                'Experimentuj s prvkami samoriadenia'
            ]
        },
        teal: {
            key: 'teal',
            label: '🩵 Tyrkysová',
            emoji: '🩵',
            color: 'text-teal-600',
            bgColor: 'bg-teal-500',
            barColor: 'bg-teal-500',
            description:
                'Samo-riadiaci živý systém. Distribuovaná autorita, evolučný purpose a celistvosť. Žiadna fixná hierarchia, plynulé roly, vzájomná zodpovednosť. Organizácia je vnímaná ako živý organizmus s vlastným zmyslom. Príklady: Buurtzorg, Morning Star.',
            characteristics: [
                'Samo-riadiace tímy bez tradičného manažmentu',
                'Rozhoduje ten, kto je najbližšie k problému (poradný proces – advice process)',
                'Organizácia má vlastný evolučný purpose',
                'Ľudia môžu byť „celí" – nie len profesionálna rola',
                'Neustála adaptácia a emergentná stratégia'
            ],
            tips: [
                'Teal nie je „lepšia" než zelená – je iná a vyžaduje zrelosť',
                'Zavádzaj teal praktiky postupne – poradný proces je dobrý začiatok',
                'Buď pripravený na odpor od ľudí zvyknutých na hierarchiu',
                'Investuj do rozvojových praktík (koučing, mindfulness, retrospektívy)'
            ]
        }
    },
    questions: [
        // 6 provided questions
        {
            title: 'Ako sa robia kľúčové rozhodnutia vo vašej organizácii?',
            options: [
                { text: 'Rozhoduje najmocnejší človek – ostatní sa prispôsobia', typeKey: 'red' },
                { text: 'Cez formálne procedúry, hierarchiu a schvaľovacie procesy', typeKey: 'amber' },
                { text: 'Lídrami zodpovednými za metriky a obchodné výsledky', typeKey: 'orange' },
                { text: 'Konsenzom alebo širokým zapojením zainteresovaných strán', typeKey: 'green' },
                { text: 'Ktokoľvek najbližšie k problému, s využitím advice procesu', typeKey: 'teal' }
            ]
        },
        {
            title: 'Čo primárne motivuje ľudí k výkonu?',
            options: [
                { text: 'Strach z dôsledkov od vedenia alebo kolegov', typeKey: 'red' },
                { text: 'Lojalita k role, systému a skupinovej identite', typeKey: 'amber' },
                { text: 'Bonusy, povýšenia a uznanie za výsledky', typeKey: 'orange' },
                { text: 'Zdieľané hodnoty, misia a pocit príslušnosti', typeKey: 'green' },
                { text: 'Osobný zmysel a prínos k poslaniu organizácie', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako vaša organizácia rieši konflikty?',
            options: [
                { text: 'Moc rozhoduje – kto má väčšiu autoritu, vyhráva', typeKey: 'red' },
                { text: 'Protokoly a hierarchia riadia spory cez správne kanály', typeKey: 'amber' },
                { text: 'Problémy sa riešia pragmaticky podľa dopadu na biznis', typeKey: 'orange' },
                { text: 'Cez dialóg, mediáciu a starostlivosť o pocity všetkých', typeKey: 'green' },
                { text: 'Cez štruktúrované vzájomné procesy medzi kolegami, bez arbitráže manažéra', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako sa vaša organizácia stavia k zmenám?',
            options: [
                { text: 'Reaktívne – veci sa menia, keď príde kríza alebo posun moci', typeKey: 'red' },
                { text: 'Rezistentne – zmena ohrozuje stabilitu a zavedené procesy', typeKey: 'amber' },
                { text: 'Strategicky – zmena je riadená pre udržanie konkurenčnej výhody', typeKey: 'orange' },
                { text: 'Kultúrne – zmenu poháňa evolúcia hodnôt a potreby ľudí', typeKey: 'green' },
                { text: 'Emergentne – organizácia sa neustále adaptuje ako živý systém', typeKey: 'teal' }
            ]
        },
        {
            title: 'Aká je primárna rola vedenia?',
            options: [
                { text: 'Velenie a kontrola – držať moc a riadiť ostatných', typeKey: 'red' },
                { text: 'Presadzovanie pravidiel a udržiavanie integrity systému', typeKey: 'amber' },
                { text: 'Nastavovanie stratégie, riadenie výsledkov a výkonu', typeKey: 'orange' },
                { text: 'Slúženie tímu, budovanie kultúry a posilňovanie ľudí', typeKey: 'green' },
                { text: 'Vytváranie priestoru pre poslanie organizácie, vedenie je distribuované', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako vaša organizácia vníma zamestnancov?',
            options: [
                { text: 'Ako prostriedky k cieľu – lojalita sa očakáva, podriadenie sa vyžaduje', typeKey: 'red' },
                { text: 'Ako nositeľov rolí – nasleduj systém, slúž inštitúcii', typeKey: 'amber' },
                { text: 'Ako ľudské zdroje – najať, riadiť, optimalizovať, udržať', typeKey: 'orange' },
                { text: 'Ako celých ľudí s hodnotami, emóciami a potrebou zmyslu', typeKey: 'green' },
                { text: 'Ako samo-riadiacich sa partnerov rovnako zodpovedných za vývoj organizácie', typeKey: 'teal' }
            ]
        },
        // 4 extended questions
        {
            title: 'Ako prúdia informácie vo vašej organizácii?',
            options: [
                { text: 'Informácie sú moc – šéf kontroluje, čo kto vie', typeKey: 'red' },
                { text: 'Zhora nadol cez formálne kanály a porady', typeKey: 'amber' },
                { text: 'Zdieľané podľa potreby – prehľady, reporty, KPI', typeKey: 'orange' },
                { text: 'Transparentne s dôrazom na otvorenú komunikáciu a spätnú väzbu', typeKey: 'green' },
                { text: 'Radikálna transparentnosť – takmer všetko je prístupné všetkým', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako sa rieši odmeňovanie a benefity?',
            options: [
                { text: 'Šéf rozhoduje podľa lojality a užitočnosti', typeKey: 'red' },
                { text: 'Tabuľkové platy podľa pozície a odpracovaných rokov', typeKey: 'amber' },
                { text: 'Výkonnostné bonusy, podiel na zisku, odmeny viazané na výsledky', typeKey: 'orange' },
                { text: 'Spravodlivé odmeny s ohľadom na rovnosť a pohodu ľudí', typeKey: 'green' },
                { text: 'Transparentné platy, odmeny nastavené kolegami alebo sebou samým', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako sa organizácia stavia k zlyhaniu a chybám?',
            options: [
                { text: 'Zlyhanie sa trestá – vinník musí byť nájdený a potrestaný', typeKey: 'red' },
                { text: 'Zlyhanie sa rieši cez formálne procesy a disciplinárne konanie', typeKey: 'amber' },
                { text: 'Zlyhanie sa analyzuje na poučenie – analýza príčin a vyvodenie záverov', typeKey: 'orange' },
                { text: 'Zlyhanie sa vníma ľudsky – dôležitejšie je, ako sa človek cíti', typeKey: 'green' },
                { text: 'Zlyhanie je súčasťou experimentovania – rýchle učenie, žiadne obviňovanie', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako vznikajú inovácie vo vašej organizácii?',
            options: [
                { text: 'Šéf rozhodne, čo sa zmení – ostatní implementujú', typeKey: 'red' },
                { text: 'Inovačný proces je formalizovaný a riadený komisiami', typeKey: 'amber' },
                {
                    text: 'Oddelenie výskumu a vývoja, inovačné programy, hackathony a porovnávanie sa s najlepšími',
                    typeKey: 'orange'
                },
                { text: 'Iniciatívy zdola podporované kultúrou psychologickej bezpečnosti', typeKey: 'green' },
                { text: 'Ktokoľvek môže začať experiment – inovácia je distribuovaná', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako sa vo vašej organizácii nastavujú priority?',
            options: [
                { text: 'Podľa toho, čo presadí najsilnejší aktér alebo aktuálny tlak', typeKey: 'red' },
                { text: 'Podľa schváleného plánu, pravidiel a formálneho poradia úloh', typeKey: 'amber' },
                { text: 'Podľa cieľov, návratnosti, trhu a merateľného dopadu', typeKey: 'orange' },
                { text: 'Podľa dohody o tom, čo najlepšie slúži ľuďom a hodnotám organizácie', typeKey: 'green' },
                { text: 'Podľa toho, čo sa javí ako ďalší prirodzený krok pre poslanie organizácie', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako vyzerá bežná porada?',
            options: [
                { text: 'Dominujú ľudia s mocou a ostatní sledujú, čo je bezpečné povedať', typeKey: 'red' },
                { text: 'Má pevný program, formálne roly a očakávaný spôsob vystupovania', typeKey: 'amber' },
                { text: 'Sústredí sa na výsledky, ukazovatele, rozhodnutia a ďalšie kroky', typeKey: 'orange' },
                { text: 'Dáva veľký priestor zapojeniu, vzťahom a spoločnému porozumeniu', typeKey: 'green' },
                { text: 'Slúži ako miesto koordinácie medzi autonómnymi ľuďmi alebo tímami', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako sa pristupuje k rolám a pracovným náplniam?',
            options: [
                { text: 'Roly sa menia podľa potrieb mocenského centra a osobnej dôvery', typeKey: 'red' },
                { text: 'Roly sú presne definované, stabilné a viazané na organizačnú štruktúru', typeKey: 'amber' },
                { text: 'Roly sa upravujú podľa výkonu, cieľov a potrieb trhu', typeKey: 'orange' },
                { text: 'Roly sa prispôsobujú silným stránkam ľudí a potrebám tímu', typeKey: 'green' },
                { text: 'Roly sú živé dohody, ktoré sa vyvíjajú podľa práce a napätí v systéme', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako sa prijímajú noví ľudia?',
            options: [
                { text: 'Rozhoduje dôvera lídra, lojalita a schopnosť zapadnúť do mocenskej dynamiky', typeKey: 'red' },
                { text: 'Podľa formálnych kritérií, kvalifikácie a správneho procesu výberu', typeKey: 'amber' },
                { text: 'Podľa kompetencií, potenciálu výkonu a prínosu k cieľom', typeKey: 'orange' },
                { text: 'Podľa súladu s hodnotami, kultúrou a schopnosti spolupracovať', typeKey: 'green' },
                { text: 'Cez vzájomné skúmanie, či človek rezonuje s poslaním a samo-riadením', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako organizácia pracuje so stratégiou?',
            options: [
                { text: 'Stratégia sa mení podľa príležitostí, hrozieb a rozhodnutí lídra', typeKey: 'red' },
                { text: 'Stratégia je dlhodobý plán, ktorý sa má disciplinovane dodržiavať', typeKey: 'amber' },
                { text: 'Stratégia je nástroj na rast, konkurenčnú výhodu a dosahovanie cieľov', typeKey: 'orange' },
                { text: 'Stratégia vzniká so zapojením ľudí a zohľadňuje širší dopad', typeKey: 'green' },
                { text: 'Stratégia sa vynára priebežne z napätí, spätnej väzby a poslania', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako sa meria úspech organizácie?',
            options: [
                { text: 'Podľa prežitia, vplyvu a schopnosti udržať kontrolu', typeKey: 'red' },
                { text: 'Podľa stability, dodržiavania pravidiel a napĺňania predpísaných úloh', typeKey: 'amber' },
                { text: 'Podľa rastu, zisku, efektivity a porovnania s konkurenciou', typeKey: 'orange' },
                { text: 'Podľa výsledkov aj kvality kultúry, dôvery a spokojnosti ľudí', typeKey: 'green' },
                { text: 'Podľa toho, či organizácia napĺňa svoje poslanie a zdravo sa vyvíja', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako sa pristupuje k pravidlám?',
            options: [
                { text: 'Pravidlá sú pružné a závisia od toho, kto ich práve presadzuje', typeKey: 'red' },
                { text: 'Pravidlá sú základ poriadku a očakáva sa ich jednotné dodržiavanie', typeKey: 'amber' },
                { text: 'Pravidlá majú podporovať výkon; keď brzdia výsledky, menia sa', typeKey: 'orange' },
                { text: 'Pravidlá majú chrániť férovosť, dôveru a dobré fungovanie komunity', typeKey: 'green' },
                { text: 'Pravidlá sú minimálne dohody, ktoré sa upravujú podľa skúsenosti systému', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako sa v organizácii prejavuje zodpovednosť?',
            options: [
                { text: 'Ľudia zodpovedajú najmä tomu, kto má moc ich odmeniť alebo potrestať', typeKey: 'red' },
                { text: 'Zodpovednosť vyplýva z pozície, popisu práce a nadriadeného', typeKey: 'amber' },
                { text: 'Zodpovednosť sa viaže na ciele, metriky a hodnotenie výkonu', typeKey: 'orange' },
                { text: 'Zodpovednosť je voči tímu, hodnotám a dohodám, ktoré ľudia spolutvoria', typeKey: 'green' },
                { text: 'Zodpovednosť je rovesnícka a vychádza z prijatých rolí a záväzkov', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako sa rieši osobný rozvoj ľudí?',
            options: [
                { text: 'Rozvoj závisí od priazne vplyvných ľudí a aktuálnej užitočnosti', typeKey: 'red' },
                { text: 'Rozvoj prebieha cez povinné školenia, kariérne stupne a pravidlá postupu', typeKey: 'amber' },
                { text: 'Rozvoj je investícia do vyššieho výkonu, talentu a budúcich lídrov', typeKey: 'orange' },
                { text: 'Rozvoj podporuje celého človeka, spoluprácu a kvalitu kultúry', typeKey: 'green' },
                { text: 'Rozvoj je súčasťou práce na sebe, celistvosti a schopnosti samo-riadenia', typeKey: 'teal' }
            ]
        },
        {
            title: 'Ako organizácia reaguje na napätie medzi tímami?',
            options: [
                { text: 'Vyhrá tím alebo líder, ktorý má väčšiu moc alebo naliehavosť', typeKey: 'red' },
                { text: 'Napätie sa eskaluje cez hierarchiu a rieši podľa pravidiel', typeKey: 'amber' },
                { text: 'Hľadá sa riešenie s najlepším dopadom na ciele, klientov a výkon', typeKey: 'orange' },
                { text: 'Dôraz je na dialóg, vzájomné porozumenie a férovú dohodu', typeKey: 'green' },
                { text: 'Tímy pracujú s napätím ako so signálom, ktorý ukazuje potrebnú zmenu', typeKey: 'teal' }
            ]
        }
    ]
}

// ---------------------------------------------------------------------------
// SURVEY 3 – Kegan's 5 Orders of Consciousness
// Source: Robert Kegan, In Over Our Heads (1994)
// Note: Order 1 is early childhood – survey covers Orders 2–5 only.
// ---------------------------------------------------------------------------

export const KEGAN_ORDERS_DATA: TallySurveyDefinition = {
    surveyType: SurveyTypes.KEGAN_ORDERS,
    title: 'Úrovne vedomia',
    subtitle: 'Kegan',
    slug: 'urovne-vedomia',
    icon: '🧬',
    duration: '10',
    description:
        'Zisti svoju úroveň kognitívnej komplexity podľa Roberta Kegana. Každá úroveň reprezentuje odlišný spôsob, akým konštruuješ zmysel sveta, vzťahov a vlastnej identity.',
    types: {
        order2: {
            key: 'order2',
            label: 'Úroveň 2 – Imperiálna myseľ',
            emoji: '🎯',
            color: 'text-orange-600',
            bgColor: 'bg-orange-500',
            barColor: 'bg-orange-500',
            description:
                'Som moje potreby, záujmy a ciele. Pravidlá dodržiavam, keď mi slúžia. Iných posudzujem primárne ako prostriedky k mojim cieľom. Bežné u adolescentov a prítomné u niektorých dospelých. Charakterizované konkrétnym myslením a ťažkosťou skutočne prevziať perspektívu iných.',
            characteristics: [
                'Svet je organizovaný okolo tvojich potrieb a záujmov',
                'Pravidlá sú nástroje – dodržiavaš ich, keď je to výhodné',
                'Vzťahy majú transakčný charakter',
                'Konflikty vnímaš ako hry s víťazom a porazeným',
                'Ťažkosť skutočne vidieť svet očami iných'
            ],
            tips: [
                'Cvič sa v aktívnom počúvaní a empatiickom naladení',
                'Skúšaj sa pýtať: „Čo by som cítil na ich mieste?"',
                'Hľadaj win-win riešenia namiesto nulového súčtu',
                'Koučing alebo mentoring ti môže pomôcť posunúť sa'
            ]
        },
        order3: {
            key: 'order3',
            label: 'Úroveň 3 – Socializovaná myseľ',
            emoji: '🤝',
            color: 'text-blue-600',
            bgColor: 'bg-blue-500',
            barColor: 'bg-blue-500',
            description:
                'Moja identita je konštruovaná vzťahmi a komunitami, ku ktorým patrím. Hlboko internalizujem očakávania a hodnoty iných. Konflikt je nepríjemný, pretože nezhoda ohrozuje moje sebavnímanie. Asi 65 % dospelých operuje tu. Silná empatia, ale závislosť na vonkajšom potvrdení.',
            characteristics: [
                'Tvoja identita je úzko prepojená so vzťahmi a skupinami',
                'Internalizuješ hodnoty a očakávania dôležitých ľudí',
                'Nezhoda s autoritami ťa hlboko znepokojuje',
                'Silná empatia a schopnosť naladenia na iných',
                'Ťažkosť odporovať skupinovému tlaku'
            ],
            tips: [
                'Skúšaj formulovať vlastné názory predtým, než počuješ ostatných',
                'Cvič sa v zdravom nesúhlase – nezhoda nemusí ohroziť vzťah',
                'Ptaj sa sám seba: „Čo si myslím JA, nie čo si myslia ONI?"',
                'Písanie denníka ti môže pomôcť rozlíšiť vlastné a cudzie hodnoty'
            ]
        },
        order4: {
            key: 'order4',
            label: 'Úroveň 4 – Seba-autorská myseľ',
            emoji: '✍️',
            color: 'text-purple-600',
            bgColor: 'bg-purple-500',
            barColor: 'bg-purple-500',
            description:
                'Mám ja, ktoré je odlíšené od očakávaní iných. Vlastním svoje hodnoty, stanovujem vlastné hranice a píšem vlastný príbeh. Môžem nesúhlasiť s autoritami a zároveň si ich vážiť. Asi 35 % dospelých. Silný vnútorný kompas; rizikom je rigidita.',
            characteristics: [
                'Jasný vnútorný hodnotový systém, ktorý dokážeš artikulovať',
                'Schopnosť nesúhlasiť s autoritami bez straty sebaúcty',
                'Autorstvo vlastného životného príbehu',
                'Schopnosť kriticky hodnotiť skupinové normy',
                'Občas rigidné držanie sa vlastných presvedčení'
            ],
            tips: [
                'Dávaj pozor na vlastnú rigiditu – tvoj framework nie je jediný správny',
                'Skúšaj zámerné vystavovanie sa radikálne odlišným pohľadom',
                'Cvič sa v držaní paradoxu – dve protichodné veci môžu byť zároveň pravdivé',
                'Hľadaj situácie, kde tvoja identita narúša tvoju flexibilitu'
            ]
        },
        order5: {
            key: 'order5',
            label: 'Úroveň 5 – Seba-transformujúca myseľ',
            emoji: '🌀',
            color: 'text-teal-600',
            bgColor: 'bg-teal-500',
            barColor: 'bg-teal-500',
            description:
                'Aj moja seba-autorská identita je konštrukcia, ktorú môžem skúmať a spochybňovať. Pohodlne držím paradox, vidím naprieč systémami a objímam protirečenie ako generatívne. Veľmi zriedkavé – typicky sa objavuje po strednom veku. Hlboký komfort s nejednoznačnosťou a limitmi akéhokoľvek frameworku, vrátane vlastného.',
            characteristics: [
                'Vidíš vlastnú identitu ako „konštrukciu v procese"',
                'Pohodlne držíš paradox a protirečenie',
                'Vidíš naprieč systémami a frameworks',
                'Hlboká pokora a otvorenosť voči neznámemu',
                'Schopnosť integrovať zdanlivo nezlučiteľné perspektívy'
            ],
            tips: [
                'Zdieľaj svoju perspektívu – ľudia na nižších úrovniach ju potrebujú',
                'Buď trpezlivý s tými, ktorí ešte nedržia paradox',
                'Udržuj si prax (meditácia, reflexia), ktorá ťa drží v tejto otvorenosti',
                'Pamätaj, že aj Order 5 je konštrukcia – nech ťa to nezastaví v raste'
            ]
        }
    },
    questions: [
        // 6 provided questions
        {
            title: 'Keď niekto, koho si hlboko vážim, nesúhlasí s mojím rozhodnutím...',
            options: [
                { text: 'Sústredím sa na to, či to ovplyvní moje ciele alebo záujmy', typeKey: 'order2' },
                { text: 'Cítim sa otrasený – ich nesúhlas na mne ťažko dolieha', typeKey: 'order3' },
                { text: 'Pozorne počúvam, ale ak ma nepresvedčia, dôverujem vlastnému úsudku', typeKey: 'order4' },
                {
                    text: 'Zvládnem to so zvedavosťou – aj moje presvedčenia môžu mať prospech z tejto výzvy',
                    typeKey: 'order5'
                },
                {
                    text: 'Zistím, čo si myslia ostatní v mojom kruhu, predtým než sa rozhodnem, ako reagovať',
                    typeKey: 'order3'
                }
            ]
        },
        {
            title: 'Keď sa ukáže, že presvedčenie, ktoré som roky mal, je nesprávne...',
            options: [
                {
                    text: 'Bránim ho alebo hľadám spôsoby, ako racionalizovať, že som mal predsa pravdu',
                    typeKey: 'order2'
                },
                { text: 'Cítim sa skutočne rozrušený – môj svetonázor je otrasený', typeKey: 'order3' },
                {
                    text: 'Aktualizujem svoje presvedčenia logicky; neotrasie to moju jadrovú identitu',
                    typeKey: 'order4'
                },
                { text: 'Vítam to – presne takto rastiem, očakávam to', typeKey: 'order5' },
                { text: 'Pozerám sa na ľudí okolo seba, aby som pochopil, čomu veriť namiesto toho', typeKey: 'order3' }
            ]
        },
        {
            title: 'Ako prežívam svoje osobné hodnoty?',
            options: [
                { text: 'Väčšinou ako to, čo je mi práve užitočné alebo prospešné', typeKey: 'order2' },
                { text: 'Ako veci, ktoré ma dôležití ľudia v mojom živote naučili – správna cesta', typeKey: 'order3' },
                {
                    text: 'Ako moje vlastné hlboko premyslené princípy, ktoré viem artikulovať a obhájiť',
                    typeKey: 'order4'
                },
                { text: 'Ako vyvíjajúce sa frameworks, ktoré držím s presvedčením aj otvorenosťou', typeKey: 'order5' },
                { text: 'Ako hodnoty skupín a komunít, s ktorými sa najviac identifikujem', typeKey: 'order3' }
            ]
        },
        {
            title: 'Ako sa staviam ku kritike alebo negatívnej spätnej väzbe?',
            options: [
                { text: 'Sústredím sa na to, či je kritik niekto, kto ovplyvňuje moje ciele', typeKey: 'order2' },
                { text: 'Často je bolestivá – beriem ju ako odraz mojej hodnoty', typeKey: 'order3' },
                { text: 'Dokážem oddeliť, kto som, od spätnej väzby a využiť ju na zlepšenie', typeKey: 'order4' },
                {
                    text: 'Som skutočne zvedavý – aj nespravodlivá kritika môže obsahovať niečo užitočné',
                    typeKey: 'order5'
                },
                {
                    text: 'Zistím, čo si o tom myslia dôveryhodní ľudia, predtým než sa rozhodnem, ako sa cítiť',
                    typeKey: 'order3'
                }
            ]
        },
        {
            title: 'Keď premýšľam o svojej identite – kto som – povedal by som...',
            options: [
                { text: 'Som to, čo chcem a na čom pracujem', typeKey: 'order2' },
                { text: 'Som moje roly: práca, vzťahy, členstvá v komunitách', typeKey: 'order3' },
                { text: 'Som človek s jasnými hodnotami a konzistentným spôsobom bytia vo svete', typeKey: 'order4' },
                {
                    text: 'Moja identita je neustály proces – všetky moje ja sú čiastkové konštrukcie',
                    typeKey: 'order5'
                },
                { text: 'Stále to zisťujem – iní ma definujú viac než ja sám seba', typeKey: 'order3' }
            ]
        },
        {
            title: 'Ako zvládam komplexitu a paradox?',
            options: [
                { text: 'Sústredím sa na to, čo funguje pre mňa alebo moju bezprostrednú skupinu', typeKey: 'order2' },
                { text: 'Preferujem jasnosť – paradox je nepohodlný a hľadám riešenie', typeKey: 'order3' },
                { text: 'Mám frameworks a hodnoty, ktoré mi pomáhajú navigovať komplexitu dobre', typeKey: 'order4' },
                { text: 'Je mi pohodlné sedieť s tenziou – paradox často obsahuje hlbšiu pravdu', typeKey: 'order5' },
                { text: 'Hľadám, čo by v tejto situácii robili ľudia, ktorých si najviac vážim', typeKey: 'order3' }
            ]
        },
        // 4 extended questions
        {
            title: 'Ako definujem úspech?',
            options: [
                { text: 'Dosiahnem to, čo chcem – peniaze, status, výhody', typeKey: 'order2' },
                { text: 'Ľudia, na ktorých mi záleží, sú na mňa hrdí a cítim, že patrím', typeKey: 'order3' },
                { text: 'Žijem v súlade s hodnotami, ktoré som si vedome zvolil', typeKey: 'order4' },
                {
                    text: 'Rastiem a prispievam spôsobom, ktorý presahuje moju aktuálnu predstavu o sebe',
                    typeKey: 'order5'
                },
                { text: 'Napĺňam očakávania mojej komunity a roly, ktorú zastávam', typeKey: 'order3' }
            ]
        },
        {
            title: 'Pri dôležitom životnom rozhodnutí (zmena kariéry, sťahovanie, rozchod)...',
            options: [
                { text: 'Vyberiem si to, čo mi prinesie najväčší osobný prospech', typeKey: 'order2' },
                { text: 'Hlboko premýšľam, čo si o tom pomyslia ľudia, na ktorých mi záleží', typeKey: 'order3' },
                { text: 'Analyzujem rozhodnutie cez moje hodnoty a dlhodobý plán', typeKey: 'order4' },
                { text: 'Držím viacero perspektív naraz a nechám rozhodnutie „dozrieť"', typeKey: 'order5' },
                { text: 'Radím sa s blízkymi a robím to, čo je v súlade s ich očakávaniami', typeKey: 'order3' }
            ]
        },
        {
            title: 'Čo sa deje, keď v niečom zlyhám?',
            options: [
                { text: 'Hľadám, koho obviniť, alebo to racionalizujem ako nie moju chybu', typeKey: 'order2' },
                { text: 'Cítim hlbokú hanbu – bojím sa, čo si o mne pomyslia', typeKey: 'order3' },
                { text: 'Analyzujem, čo som mohol urobiť inak, a nastavím lepší systém', typeKey: 'order4' },
                { text: 'Zaujme ma, čo mi zlyhanie hovorí o limitoch môjho frameworku', typeKey: 'order5' },
                { text: 'Sledujem reakciu okolia a prispôsobím sa tomu, čo je „správne"', typeKey: 'order3' }
            ]
        },
        {
            title: 'Pri morálnej dileme (napr. čestnosť vs. lojalita)...',
            options: [
                { text: 'Vyberiem si to, čo je pre mňa najvýhodnejšie', typeKey: 'order2' },
                { text: 'Robím to, čo by schválili ľudia a skupiny, ku ktorým patrím', typeKey: 'order3' },
                { text: 'Rozhodujem sa podľa mojich vnútorných princípov, aj keď sú nepopulárne', typeKey: 'order4' },
                { text: 'Vidím, že obe strany majú čiastkovú pravdu, a hľadám integráciu', typeKey: 'order5' },
                { text: 'Hľadám, čo je v mojom prostredí považované za morálne správne', typeKey: 'order3' }
            ]
        },
        {
            title: 'Keď sa ocitnem v skupine s veľmi odlišnými názormi...',
            options: [
                { text: 'Sledujem hlavne, ktorý názor najlepšie podporí moje záujmy alebo plán', typeKey: 'order2' },
                {
                    text: 'Hľadám stanovisko, pri ktorom zostanem v dobrom vzťahu s ľuďmi, na ktorých mi záleží',
                    typeKey: 'order3'
                },
                { text: 'Porovnám názory s vlastnými princípmi a rozhodnem sa, za čím si stojím', typeKey: 'order4' },
                { text: 'Skúmam, aké predpoklady má každý názor vrátane môjho vlastného', typeKey: 'order5' }
            ]
        },
        {
            title: 'Keď dostanem príležitosť, ktorá je lákavá, ale nie úplne v súlade s mojím smerovaním...',
            options: [
                { text: 'Zvažujem najmä, čo mi môže priniesť a či sa mi to oplatí', typeKey: 'order2' },
                { text: 'Premýšľam, ako by moje rozhodnutie prijali dôležití ľudia v mojom okolí', typeKey: 'order3' },
                { text: 'Overím si, či zapadá do hodnôt a cieľov, ktoré som si vedome zvolil', typeKey: 'order4' },
                {
                    text: 'Vnímam ju ako možnosť preskúmať, či moje doterajšie smerovanie netreba prehodnotiť',
                    typeKey: 'order5'
                }
            ]
        },
        {
            title: 'Keď niekto spochybní môj spôsob života alebo práce...',
            options: [
                { text: 'Zaujíma ma, či má jeho názor praktický dopad na moje možnosti', typeKey: 'order2' },
                {
                    text: 'Dotkne sa ma to, najmä ak ide o človeka, ktorého uznanie je pre mňa dôležité',
                    typeKey: 'order3'
                },
                { text: 'Vypočujem si ho, ale rozhodujem sa podľa vlastného kompasu', typeKey: 'order4' },
                { text: 'Použijem to ako zrkadlo na preskúmanie svojich predpokladov', typeKey: 'order5' }
            ]
        },
        {
            title: 'Keď musím nastaviť hranicu vo vzťahu...',
            options: [
                { text: 'Nastavím ju podľa toho, čo potrebujem a čo je pre mňa výhodné', typeKey: 'order2' },
                { text: 'Je to pre mňa ťažké, lebo nechcem narušiť blízkosť alebo sklamať druhého', typeKey: 'order3' },
                { text: 'Pomenujem ju jasne, aj keď to môže byť nepríjemné', typeKey: 'order4' },
                { text: 'Vnímam hranicu aj vzťah ako živý proces, ktorý sa môže ďalej vyvíjať', typeKey: 'order5' }
            ]
        },
        {
            title: 'Keď sa moje potreby dostanú do konfliktu s potrebami iných...',
            options: [
                { text: 'Hľadám riešenie, ktoré čo najviac ochráni moje záujmy', typeKey: 'order2' },
                { text: 'Snažím sa nájsť riešenie, pri ktorom nikto dôležitý nebude sklamaný', typeKey: 'order3' },
                { text: 'Zvažujem konflikt cez svoje princípy férovosti a zodpovednosti', typeKey: 'order4' },
                { text: 'Skúmam, čo konflikt odhaľuje o systéme vzťahov a rolí medzi nami', typeKey: 'order5' }
            ]
        },
        {
            title: 'Keď spätne hodnotím veľké rozhodnutie, ktoré som urobil...',
            options: [
                { text: 'Pozerám sa najmä na to, či mi prinieslo očakávaný výsledok', typeKey: 'order2' },
                { text: 'Premýšľam, či som tým nesklamal ľudí alebo skupiny, ku ktorým patrím', typeKey: 'order3' },
                { text: 'Hodnotím, či bolo konzistentné s tým, kým chcem byť', typeKey: 'order4' },
                { text: 'Zaujíma ma, ako sa cez toto rozhodnutie zmenilo moje chápanie seba', typeKey: 'order5' }
            ]
        },
        {
            title: 'Keď sa ocitnem mimo známeho prostredia...',
            options: [
                { text: 'Rýchlo zisťujem, čo funguje a ako sa v tom viem presadiť', typeKey: 'order2' },
                { text: 'Hľadám signály, ako sa správať, aby som zapadol', typeKey: 'order3' },
                { text: 'Držím sa vlastných hodnôt, aj keď normy okolia sú iné', typeKey: 'order4' },
                {
                    text: 'Všímam si, ako nové prostredie odhaľuje limity mojich zaužívaných kategórií',
                    typeKey: 'order5'
                }
            ]
        },
        {
            title: 'Keď niekto odo mňa očakáva niečo, s čím nesúhlasím...',
            options: [
                { text: 'Zvažujem, či sa mi oplatí vyhovieť alebo odmietnuť', typeKey: 'order2' },
                { text: 'Cítim tlak vyhovieť, najmä ak je pre mňa vzťah dôležitý', typeKey: 'order3' },
                { text: 'Viem povedať nie, ak je to v rozpore s mojimi princípmi', typeKey: 'order4' },
                { text: 'Skúmam, čo toto očakávanie hovorí o našich rolách a mojich hraniciach', typeKey: 'order5' }
            ]
        },
        {
            title: 'Keď sa mení niečo, na čom som si zakladal...',
            options: [
                { text: 'Sústredím sa na to, ako si čo najrýchlejšie zabezpečiť výhodnú pozíciu', typeKey: 'order2' },
                {
                    text: 'Najviac ma trápi, či tým stratím uznanie alebo miesto medzi svojimi ľuďmi',
                    typeKey: 'order3'
                },
                { text: 'Prehodnotím plán, ale opieram sa o stabilné vnútorné princípy', typeKey: 'order4' },
                { text: 'Pýtam sa, či sa nemení aj samotný rámec, cez ktorý som situáciu chápal', typeKey: 'order5' }
            ]
        },
        {
            title: 'Keď mám pomenovať, čo je pre mňa pravda...',
            options: [
                {
                    text: 'Je to to, čo v danej situácii funguje a dáva mi konkrétny zisk alebo ochranu',
                    typeKey: 'order2'
                },
                { text: 'Je to to, čo potvrdzujú ľudia a komunity, ktorým dôverujem', typeKey: 'order3' },
                { text: 'Je to to, čo obstojí voči mojim premysleným princípom a skúsenosti', typeKey: 'order4' },
                { text: 'Je to dočasné porozumenie, ktoré držím pevne aj otvorene voči zmene', typeKey: 'order5' }
            ]
        }
    ]
}

// ---------------------------------------------------------------------------
// SURVEY 4 – 3 Levels of Listening
// Source: Co-Active Training Institute (CTI) framework
// ---------------------------------------------------------------------------

export const LISTENING_LEVELS_DATA: TallySurveyDefinition = {
    surveyType: SurveyTypes.LISTENING_LEVELS,
    title: 'Úrovne počúvania',
    subtitle: 'CTI',
    slug: 'urovne-pocuvania',
    icon: '👂',
    duration: '6',
    description:
        'Zisti, na akej úrovni počúvaš najčastejšie podľa Co-Active koučingového modelu. Úrovne nie sú „lepšie alebo horšie" – každá má svoje miesto, ale väčšina ľudí uviazne na Level 1.',
    types: {
        level1: {
            key: 'level1',
            label: 'Úroveň 1 – Interné počúvanie',
            emoji: '💭',
            color: 'text-orange-600',
            bgColor: 'bg-orange-500',
            barColor: 'bg-orange-500',
            description:
                'Počúvaš primárne cez vlastnú optiku – porovnávaš, plánuješ odpoveď, filtruješ cez vlastnú skúsenosť. Toto je predvolený režim väčšiny ľudí vo väčšine rozhovorov. Slúži ti, keď potrebuješ udržať svoju pozíciu, ale limituje skutočné spojenie a učenie sa. Hlas vnútri tvojej hlavy je hlasnejší ako človek pred tebou.',
            characteristics: [
                'Tvoja myseľ plánuje odpoveď, kým druhý ešte hovorí',
                'Prepájaš všetko so svojou vlastnou skúsenosťou',
                'Máš tendenciu skákať do reči alebo dokončovať vety',
                'Po rozhovore si pamätáš hlavne, čo si povedal ty',
                'Počúvaš, aby si potvrdil, čo už vieš'
            ],
            tips: [
                'Skús experiment: 1 deň sa v rozhovoroch pýtaj len otázky',
                'Keď ťa napadne odpoveď, zapíš si ju a vráť sa k počúvaniu',
                'Pred odpoveďou parafrázuj, čo si počul',
                'Cvič sa v 3-sekundovej pauze pred každou odpoveďou',
                'Všimni si, koľkokrát denne povieš „u mňa to bolo tak, že..."'
            ]
        },
        level2: {
            key: 'level2',
            label: 'Úroveň 2 – Sústredené počúvanie',
            emoji: '🎯',
            color: 'text-blue-600',
            bgColor: 'bg-blue-500',
            barColor: 'bg-blue-500',
            description:
                'Si plne prítomný k druhému človeku – zvedavý, pozorný na jeho slová, tón a emocionálny podtext. Nič ťa nerozptyľuje. Ľudia sa okolo teba cítia skutočne vypočutí, čo buduje dôveru a otvára hlbší rozhovor. Tu žije skutočné porozumenie. Najlepší lídri, koučovia a partneri sa na tejto úrovni pohybujú bežne.',
            characteristics: [
                'Si plne prítomný a sústredený na druhého',
                'Kladieš premyslené nadväzujúce otázky',
                'Vnímaš nielen slová, ale aj tón a emócie',
                'Ľudia sa okolo teba cítia vypočutí a pochopení',
                'Po rozhovore dokážeš presne zopakovať, čo druhý povedal'
            ],
            tips: [
                'Tento štýl je tvoja silná stránka – rozvíjaj ho ďalej',
                'Skús si všímať aj to, čo NIE JE povedané (Level 3)',
                'Cvič sa v počúvaní „za" slová – čo človek skutočne potrebuje?',
                'Zdieľaj túto zručnosť – uč iných počúvať',
                'Buď si vedomý momentov, keď sklzneš späť na Level 1'
            ]
        },
        level3: {
            key: 'level3',
            label: 'Úroveň 3 – Globálne počúvanie',
            emoji: '🌐',
            color: 'text-teal-600',
            bgColor: 'bg-teal-500',
            barColor: 'bg-teal-500',
            description:
                'Si naladený na celé pole rozhovoru: čo je povedané aj nepovedané, energetické posuny, reč tela, atmosféra miestnosti. Vnímaš systém, nie len signál. Počuješ, čo ľudia ešte nedokážu vyjadriť slovami. Zriedkavé a mocné – najprirodzenejšie pre skúsených koučov a ľudí so silnou intuitívnou inteligenciou.',
            characteristics: [
                'Vnímaš energiu, atmosféru a dynamiku v miestnosti',
                'Všímaš si neverbálne signály a „to, čo visí vo vzduchu"',
                'Dokážeš pomenovať, čo skupina alebo človek ešte nevie vyjadriť',
                'Tvoja intuícia ťa často vedie správnym smerom',
                'Ľudia sa okolo teba cítia „videní" na hlbšej úrovni'
            ],
            tips: [
                'Toto je vzácna schopnosť – váž si ju a vedome ju používaj',
                'Overuj si svoje intuície otázkami – intuícia nie je vždy správna',
                'Zdieľaj, čo vnímaš, jemne – „Mám pocit, že..."',
                'Uč iných vnímať neverbálnu komunikáciu',
                'Dávaj pozor na interpretačné skreslenie – tvoja intuícia je filter, nie fakt'
            ]
        }
    },
    questions: [
        {
            title: 'Kolega ti na obede rozpráva o konflikte v tíme. Čo sa deje v tvojej hlave?',
            options: [
                {
                    text: 'Hneď mi napadne, čo by som na jeho mieste urobil, a čakám na moment, kedy to môžem povedať',
                    typeKey: 'level1'
                },
                {
                    text: 'Pýtam sa, čo presne povedali a ako to na neho zapôsobilo, aby som pochopil celý obraz',
                    typeKey: 'level2'
                },
                {
                    text: 'Registrujem, že jeho hlas stvrdol pri jednom mene, a zaujíma ma, čo za tým je',
                    typeKey: 'level3'
                }
            ]
        },
        {
            title: 'Kamarátka ti volá a hovorí, že zvažuje odísť z práce. Ako reaguješ?',
            options: [
                {
                    text: 'Poviem jej, čo by som robil ja, alebo jej hneď pošlem ponuky, ktoré som videl',
                    typeKey: 'level1'
                },
                {
                    text: 'Nechám ju hovoriť a overím si, čo ju trápi najviac, skôr než čokoľvek navrhujem',
                    typeKey: 'level2'
                },
                {
                    text: 'Všimnem si, že vlastne neprosí o radu: potrebuje, aby niekto potvrdil to, čo už cíti',
                    typeKey: 'level3'
                }
            ]
        },
        {
            title: 'Počas prezentácie kolegyňa zaváha a stratí niť. Čo urobíš?',
            options: [
                {
                    text: 'Skočím jej do reči a pomôžem dokončiť myšlienku, aby sa situácia zachránila',
                    typeKey: 'level1'
                },
                {
                    text: 'Počkám, kým sa vráti k téme, a potom položím otázku, ktorá nadväzuje na jej hlavný bod',
                    typeKey: 'level2'
                },
                {
                    text: 'Všimnem si, že napätie v miestnosti kleslo: skupina je unavená a zaváhanie bolo len symptóm',
                    typeKey: 'level3'
                }
            ]
        },
        {
            title: 'Na tímovom stretnutí navrhneš riešenie a šéf ho bez vysvetlenia zamietne. Čo sa v tebe deje?',
            options: [
                { text: 'Premýšľam, ako svoj návrh preformulovať, aby prešiel nabudúce', typeKey: 'level1' },
                { text: 'Zaujíma ma, čo za jeho zamietnutím je: spýtam sa na jeho pohľad', typeKey: 'level2' },
                {
                    text: 'Zaregistrujem, že zamietol aj dva predchádzajúce návrhy. Niečo ho tlačí a nie je to môj nápad',
                    typeKey: 'level3'
                }
            ]
        },
        {
            title: 'Niekto blízky ti hovorí, že je v pohode, ale podľa teba nie je. Ako to riešiš?',
            options: [
                {
                    text: 'Beriem to za slovo: keď hovorí, že je v pohode, nemám dôvod to spochybňovať',
                    typeKey: 'level1'
                },
                {
                    text: 'Spýtam sa ešte raz inak: „Naozaj? Pôsobíš na mňa trochu inak ako zvyčajne"',
                    typeKey: 'level2'
                },
                {
                    text: 'Nehovorím nič, len zmením tón alebo spomalím. Väčšinou sa potom sami otvoria',
                    typeKey: 'level3'
                }
            ]
        },
        {
            title: 'Po náročnom rozhovore s niekým si zvyčajne spomínaš na...',
            options: [
                { text: 'Argumenty, ktoré som použil, a či som dokázal, čo som chcel', typeKey: 'level1' },
                { text: 'Čo presne ten druhý povedal a aký to na neho malo dopad', typeKey: 'level2' },
                {
                    text: 'Moment, keď sa v rozhovore niečo posunulo: zmena v tóne, nečakaná pauza alebo pohľad',
                    typeKey: 'level3'
                }
            ]
        },
        {
            title: 'Sedíš na porade, kde sa dvaja kolegovia nezhodnú. Čo robíš?',
            options: [
                { text: 'Vyberiem si stranu, ktorá mi dáva väčší zmysel, a podporím ju argumentmi', typeKey: 'level1' },
                { text: 'Snažím sa pochopiť obe strany a hľadám, kde sa ich pohľady stretávajú', typeKey: 'level2' },
                {
                    text: 'Zaujíma ma, prečo sa spor vôbec rozhorel práve teraz: čo sa zmenilo v tíme',
                    typeKey: 'level3'
                }
            ]
        },
        {
            title: 'Priateľ ti rozpráva dlhý príbeh a ty nevieš, kam tým smeruje. Čo spravíš?',
            options: [
                {
                    text: 'Jemne ho usmerním otázkou typu „Takže čo sa stalo potom?", aby sme sa dostali k veci',
                    typeKey: 'level1'
                },
                { text: 'Zostávam pri ňom a pozorne počúvam, aj keď zatiaľ nevidím pointu', typeKey: 'level2' },
                {
                    text: 'Všímam si, že samotné rozprávanie je pre neho dôležité: nie pointa, ale to, že mu niekto venuje čas',
                    typeKey: 'level3'
                }
            ]
        },
        {
            title: 'Kolegyňa sa na teba usmeje a povie, že projekt ide super. Ale minulý týždeň mala slzy v očiach. Čo si pomyslíš?',
            options: [
                { text: 'Asi sa to vyriešilo. Keď hovorí, že je to super, tak to bude v poriadku', typeKey: 'level1' },
                { text: 'Opýtam sa na konkrétne veci: čo sa zmenilo od minulého týždňa', typeKey: 'level2' },
                {
                    text: 'Neriešim slová. Sledujem, či sa ten úsmev dostane aj do očí, a podľa toho sa rozhodnem, ako pokračovať',
                    typeKey: 'level3'
                }
            ]
        },
        {
            title: 'Práve si mal ťažký deň a niekto sa ťa pýta, ako sa máš. Ako to ovplyvní tvoje počúvanie v ďalšom rozhovore?',
            options: [
                {
                    text: 'Ťažko sa sústredím na druhých, keď mám plnú hlavu vlastných vecí: potrebujem si najprv urovnať myšlienky',
                    typeKey: 'level1'
                },
                { text: 'Vedome odložím svoje témy a sústredím sa na človeka predo mnou', typeKey: 'level2' },
                {
                    text: 'Využijem vlastnú skúsenosť z toho dňa ako rezonančnú dosku: cítim druhého ostrejšie, pretože som sám otvorený',
                    typeKey: 'level3'
                }
            ]
        },
        {
            title: 'Počas rozhovoru druhý človek na chvíľu stíchne. Čo spravíš?',
            options: [
                { text: 'Vyplním ticho vlastnou myšlienkou alebo otázkou, aby rozhovor nestál', typeKey: 'level1' },
                { text: 'Dám mu priestor a zostanem pri tom, čo práve povedal', typeKey: 'level2' },
                { text: 'Vnímam, čo sa v tichu zmenilo: napätie, úľava alebo niečo nevyslovené', typeKey: 'level3' }
            ]
        },
        {
            title: 'Niekto ti opisuje problém, ktorý si už zažil aj ty...',
            options: [
                { text: 'Rýchlo si spomeniem na svoj príbeh a mám chuť ho zdieľať', typeKey: 'level1' },
                { text: 'Sústredím sa na to, ako tú situáciu prežíva on, nie ja', typeKey: 'level2' },
                { text: 'Vnímam podobnosti aj rozdiely a sledujem, čo sa v rozhovore práve otvára', typeKey: 'level3' }
            ]
        },
        {
            title: 'Na stretnutí niekto povie technicky správnu vec, ale miestnosť zareaguje rozpačito...',
            options: [
                { text: 'Zameriam sa na obsah a premýšľam, či má pravdu', typeKey: 'level1' },
                { text: 'Overím si, ako jeho výrok pochopili ostatní', typeKey: 'level2' },
                {
                    text: 'Všimnem si rozdiel medzi slovami a reakciou skupiny a pomenujem napätie jemne',
                    typeKey: 'level3'
                }
            ]
        },
        {
            title: 'Keď niekto hovorí pomaly a dlho hľadá slová...',
            options: [
                { text: 'V duchu dokončujem vety a rozmýšľam, ako to povedať presnejšie', typeKey: 'level1' },
                { text: 'Trpezlivo sledujem jeho formulácie a pýtam sa až potom', typeKey: 'level2' },
                { text: 'Vnímam, či je pomalosť neistota, opatrnosť alebo dôležitosť témy', typeKey: 'level3' }
            ]
        },
        {
            title: 'Partner alebo kolega ti dá spätnú väzbu, ktorá sa ti nepočúva ľahko...',
            options: [
                { text: 'Začnem si v hlave pripravovať obhajobu alebo vysvetlenie', typeKey: 'level1' },
                { text: 'Snažím sa najprv presne pochopiť, čo mi chce povedať', typeKey: 'level2' },
                {
                    text: 'Vnímam aj odvahu, napätie a potrebu za tým, že tú spätnú väzbu vôbec prináša',
                    typeKey: 'level3'
                }
            ]
        },
        {
            title: 'V skupine niekto začne rozprávať vtip, ktorý trochu odľahčí napätú situáciu...',
            options: [
                { text: 'Zareagujem na vtip a som rád, že atmosféra je ľahšia', typeKey: 'level1' },
                { text: 'Všimnem si, ako sa človek cíti a či tým nechce niečo prekryť', typeKey: 'level2' },
                {
                    text: 'Sledujem, čo humor spravil s celou skupinou a či sa tým niečo dôležité neobišlo',
                    typeKey: 'level3'
                }
            ]
        },
        {
            title: 'Niekto ti hovorí o úspechu, ale pôsobí pritom vyčerpane...',
            options: [
                { text: 'Zablahoželám a porovnám to so svojimi skúsenosťami s úspechom', typeKey: 'level1' },
                { text: 'Pýtam sa, čo pre neho ten úspech znamená a čo ho stál', typeKey: 'level2' },
                { text: 'Vnímam rozpor medzi obsahom a energiou a opatrne ho skúmam', typeKey: 'level3' }
            ]
        },
        {
            title: 'Počas rozhovoru si všimneš, že ťa napadla výborná rada...',
            options: [
                { text: 'Čakám na najbližší vhodný moment, aby som ju povedal', typeKey: 'level1' },
                { text: 'Odložím ju a najprv si overím, či druhý človek radu vôbec chce', typeKey: 'level2' },
                {
                    text: 'Skúmam, či moja chuť radiť nevznikla z nepohody v tom, čo sa deje medzi nami',
                    typeKey: 'level3'
                }
            ]
        },
        {
            title: 'Na porade niekto opakovane mlčí, hoci téma sa ho týka...',
            options: [
                { text: 'Predpokladám, že nemá čo dodať alebo s tým súhlasí', typeKey: 'level1' },
                { text: 'Dám mu priestor otázkou, či chce doplniť svoj pohľad', typeKey: 'level2' },
                { text: 'Vnímam, čo v skupine môže spôsobovať, že jeho hlas nezaznieva', typeKey: 'level3' }
            ]
        },
        {
            title: 'Keď rozhovor skončí, najčastejšie vieš pomenovať...',
            options: [
                { text: 'Čo som povedal dobre a čo som ešte mohol doplniť', typeKey: 'level1' },
                { text: 'Čo bolo pre druhého človeka hlavné a ako sa cítil', typeKey: 'level2' },
                { text: 'Aký posun nastal v atmosfére, dôvere alebo nevyslovenej téme rozhovoru', typeKey: 'level3' }
            ]
        }
    ]
}

// Convenience map for lookup by survey type
export const TALLY_SURVEY_DATA: Record<string, TallySurveyDefinition> = {
    [SurveyTypes.LEADERSHIP_STYLES]: LEADERSHIP_STYLES_DATA,
    [SurveyTypes.LALOUX_PARADIGMS]: LALOUX_PARADIGMS_DATA,
    [SurveyTypes.KEGAN_ORDERS]: KEGAN_ORDERS_DATA,
    [SurveyTypes.LISTENING_LEVELS]: LISTENING_LEVELS_DATA
}
